import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../services/general/alert.service';
import { SurveyService } from '../../../services/surveys.service';
import { ToastService } from '../../../services/general/toast.service';
import { ModalEmailResponsesComponent } from '../../../modals/modal-email-responses/modal-email-responses.component';
import { MatDialog } from '@angular/material/dialog';
import { HelperService } from '../../../services/general/helper.service';
import { EChartsOption } from 'echarts';
// Ho aggiunto alcune icone Material per coerenza col nuovo design, mantenendo le FA necessarie
import { faSearch, faBullhorn, faTrash, faPlusCircle, faTimes, faDownload, faColumns, faEllipsisV, faHistory, faExternalLinkAlt, faUserSecret, faEdit, faFileExcel, faEnvelope, faLink, faEye, faUsers } from '@fortawesome/free-solid-svg-icons';
import {
  ModalViewSentSurveyQuestionnaireComponent
} from "../../../modals/modal-view-sent-survey-questionnaire/modal-view-sent-survey-questionnaire.component";
import {ModalImportSurveyTeamComponent} from "../../../modals/modal-import-survey-team/modal-import-survey-team.component";

interface TeamMember {
  id: number | string;
  email: string;
  name: string;
  last_name?: string;
  response_status: string;
  last_status_invitation_email: string;
  invitation_link: string;
  role?: string;
  is_respondent?: boolean;
  customs?: any;
}

@Component({
  selector: 'app-survey-team',
  templateUrl: './survey-team.component.html',
  styleUrls: ['./survey-team.component.scss'],
  standalone: false,
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
  ],
})
export class SurveyTeamComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() members: TeamMember[] = [];
  @Input() surveyId: number | string | null = null;
  @Input() surveyIsClosed: boolean = false;
  @Input() isPublic: boolean = false;
  @Input() teamMemberCustomFields: any[] = [];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<TeamMember>([]);
  selectedEmailIds = new Set<number | string>();

  displayedColumns = ['expand', 'select', 'member_info', 'role', 'is_respondent', 'response_status', 'email_status', 'actions'];
  private baseColumns = ['expand', 'select', 'member_info', 'role', 'is_respondent', 'response_status', 'email_status', 'actions'];
  columnSettings: { key: string, label: string, visible: boolean, mandatory?: boolean }[] = [
    { key: 'member_info', label: 'generic.name', visible: true, mandatory: true },
    { key: 'role', label: 'generic.roles.role', visible: true },
    { key: 'is_respondent', label: 'generic.is-respondent', visible: true },
    { key: 'phone_number', label: 'generic.phone', visible: false },
    { key: 'response_status', label: 'generic.response', visible: true },
    { key: 'email_status', label: 'Email', visible: true },
  ];

  expandedElement: TeamMember | null = null;
  editingMember: any = null;

  singleTeamMemberToAddActive = false;
  loading = false;
  emailStatuses: string[] = [];
  roles: string[] = [];

  answeredCount = 0;
  draftCount = 0;
  notAnsweredCount = 0;

  filters: any = {
    responseStatus: 'ALL',
    emailStatus: [],
    searchValue: '',
    role: [],
    isRespondent: [],
    custom: {} // Stores filter values for each custom field ID
  };

  chartOptions!: EChartsOption;

  readonly faColumns = faColumns;
  readonly faEnvelope = faEnvelope;
  readonly faUsers = faUsers;
  readonly faLink = faLink;
  readonly faEye = faEye;
  readonly faBullhorn = faBullhorn;
  readonly faPlusCircle = faPlusCircle;
  readonly faTimes = faTimes;
  readonly faDownload = faDownload;
  readonly faTrash = faTrash;
  readonly faEdit = faEdit;
  readonly faFileExcel = faFileExcel;
  readonly faSearch = faSearch;
  readonly faHistory = faHistory;
  readonly faEllipsisV = faEllipsisV;
  readonly faExternalLinkAlt = faExternalLinkAlt;
  readonly faUserSecret = faUserSecret;

  @Output() refresh = new EventEmitter<void>();

  constructor(
    private translateService: TranslateService,
    private alert: AlertService,
    private surveyService: SurveyService,
    private toast: ToastService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.rebuildColumns();
    this.initDataSource();
    this.renderChart();
    this.emailStatuses = Array.from(
      new Set(
        (this.members || [])
          .map(m => m.last_status_invitation_email)
          .filter(e => !!e)
      )
    );
    this.roles = Array.from(
      new Set(
        (this.members || [])
          .map(m => m.role)
          .filter(r => !!r)
      )
    );
    this.initializeFilters();
  }

  get filteredColumnSettings() {
    return this.isPublic
      ? this.columnSettings.filter(s => s.key !== 'email_status')
      : this.columnSettings;
  }

  private initializeFilters(): void {
    (this.teamMemberCustomFields || []).forEach(f => {
      if (this.filters.custom[f.id] === undefined) {
        if (f.type === 'number' || f.type === 'date') {
          this.filters.custom[f.id] = { min: null, max: null };
        } else if (f.type === 'text') {
          this.filters.custom[f.id] = [];
        } else {
          this.filters.custom[f.id] = 'ALL';
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'member_info': return item.name;
        default: return item[property];
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.members) {
      this.dataSource.data = this.members || [];
      this.roles = Array.from(
        new Set(
          (this.members || [])
            .map(m => m.role)
            .filter(r => !!r)
        )
      );
      this.renderChart();
    }
    if (changes.teamMemberCustomFields || changes.isPublic || changes.surveyIsClosed) {
      this.rebuildColumns();
      this.initializeFilters();
    }
  }

  private rebuildColumns(): void {
    // 1. Ensure all custom fields have a setting
    (this.teamMemberCustomFields || []).forEach(f => {
      const key = `cf_${f.id}`;
      if (!this.columnSettings.find(s => s.key === key)) {
        this.columnSettings.push({ key: key, label: f.name, visible: false });
      } else {
        // Update label if it changed
        const setting = this.columnSettings.find(s => s.key === key);
        if (setting) setting.label = f.name;
      }
    });

    // 2. Filter out obsolete custom field settings
    const currentCfKeys = (this.teamMemberCustomFields || []).map(f => `cf_${f.id}`);
    this.columnSettings = this.columnSettings.filter(s => !s.key.startsWith('cf_') || currentCfKeys.includes(s.key));

    // 3. Build displayedColumns based on visibility
    let base = [...this.baseColumns];
    if (this.surveyIsClosed || this.isPublic) {
      base = base.filter(c => c !== 'select');
    }
    if (this.isPublic) {
      base = base.filter(c => c !== 'email_status');
    }

    const availableColumnSettings = this.isPublic
      ? this.columnSettings.filter(s => s.key !== 'email_status')
      : this.columnSettings;

    const visibleKeys = availableColumnSettings.filter(s => s.visible).map(s => s.key);

    this.displayedColumns = [
      'expand',
      ...(base.includes('select') ? ['select'] : []),
      ...visibleKeys,
      'actions'
    ];
  }

  toggleColumn(setting: any): void {
    setting.visible = !setting.visible;
    this.rebuildColumns();
  }

  getCustomFieldValue(member: TeamMember, field: any): any {
    const nested = (member as any).customs?.team_member_custom_fields;
    if (!nested) return '-';
    const val = nested[field.id];
    if (val === undefined || val === null || val === '') return '-';
    if (field.type === 'boolean') return val ? 'Sì' : 'No';
    return val;
  }

  private initDataSource(): void {
    this.dataSource = new MatTableDataSource<TeamMember>(this.members || []);
    this.dataSource.filterPredicate = this.createFilterPredicate();
  }

  applyFilter() {
    this.dataSource.filter = JSON.stringify({
      status: this.filters.responseStatus.toUpperCase() || 'ALL',
      search: this.filters.searchValue,
      emailStatus: this.filters.emailStatus || [],
      role: this.filters.role || [],
      isRespondent: this.filters.isRespondent || [],
      custom: this.filters.custom || {}
    });

    this.selectedEmailIds.clear();
  }

  toggleAddNewTeamMemberMode(): void {
    this.singleTeamMemberToAddActive = !this.singleTeamMemberToAddActive;
  }

  onAddTeamMember(teamMember: TeamMember): void {
    this.toggleAddNewTeamMemberMode();
    this.members.push(teamMember);
    this.dataSource.data = this.members;
    this.renderChart();
  }

  resendInvitation(list, success_callback = (data: any) => { }): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant('components.decision-table.show-decision-table.alert.resend-invitation'),
      (ok: boolean) => {
        if (!ok) return;
        this.surveyService.resendInvitations(this.surveyId, list, (result) => {
          this.toast.success(this.translateService.instant('components.decision-table.show-decision-table.alert.invitation-sent'));
          success_callback(result);
        });
      }
    );
  }

  copyLink(link: string): void {
    navigator.clipboard.writeText(link);
    this.toast.success(this.translateService.instant('generic.success'), this.translateService.instant('generic.link-copied'));
  }

  deleteTeamMembers(list, success_callback = (data: any) => { }): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant('components.surveys.survey-team.survey-team.remove-team-member-alert'),
      (ok: boolean) => {
        if (!ok) return;
        this.loading = true;
        this.surveyService.deleteTeamMembers(this.surveyId, list, (result) => {
          this.loading = false;
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.surveys.survey-team.survey-team.remove-team-member-alert-successfully'));
          this.members = this.members.filter((member) => !list.includes(member.id)); // Fix logica filter
          this.dataSource.data = this.members;
          this.renderChart();
          success_callback(result);
        }, (error: any) => {
          this.loading = false;
        });
      }
    );
  }

  showEmailResponses(row: TeamMember | null): void {
    this.dialog.open(ModalEmailResponsesComponent, {
      panelClass: 'fullscreen-dialog',
      data: { team_member_id: row.id, origin_id: this.surveyId, origin: 'SURVEY' }
    });
  }

  emailColorByTeamMember(row: TeamMember | null): string {
    if (row.last_status_invitation_email) {
      return HelperService.getColorByEmailEvent(row.last_status_invitation_email);
    }
    return '#e0e0e0';
  }

  downloadSurveyTeamDataCSV(): void {
    this.loading = true;
    const payload = Array.from(this.selectedEmailIds);
    this.surveyService.downloadSurveyTeamData(this.surveyId, payload, () => {
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  isEmailSelected(row: TeamMember | null): boolean {
    return !!row && this.selectedEmailIds.has(row.id);
  }

  resendSelectedInvitations(): void {
    if (!this.dataSource || this.selectedEmailIds.size === 0) return;
    const ids = Array.from(this.selectedEmailIds);
    this.resendInvitation(ids, () => {
      this.selectedEmailIds = new Set();
      this.cdr.markForCheck();
    });
  }

  deleteSelectedMembers(): void {
    if (!this.dataSource || this.selectedEmailIds.size === 0) return;
    // Rimossa restrizione < 2 per permettere cancellazione libera se desiderata, o mantienila se business logic
    if (this.members?.length < 2) return;
    const ids = Array.from(this.selectedEmailIds);
    this.deleteTeamMembers(ids, () => {
      this.selectedEmailIds = new Set();
      this.cdr.markForCheck();
    });
  }

  isAllSelected(): boolean {
    const filtered = this.filteredRows();
    return filtered.length > 0 && filtered.every(row => this.selectedEmailIds.has(row.id));
  }

  isSomeSelected(): boolean {
    const filtered = this.filteredRows();
    const selectedCount = filtered.filter(row => this.selectedEmailIds.has(row.id)).length;
    return selectedCount > 0 && selectedCount < filtered.length;
  }

  toggleSelectEmail(row: TeamMember): void {
    const next = new Set(this.selectedEmailIds);
    this.selectedEmailIds.has(row.id) ? next.delete(row.id) : next.add(row.id);
    this.selectedEmailIds = next;
    this.cdr.markForCheck();
  }

  toggleSelectAll(event: { checked: boolean }): void {
    event.checked ? this.selectedEmailIds = new Set(this.filteredRows().map(r => r.id)) : this.selectedEmailIds = new Set();
    this.cdr.markForCheck();
  }

  filteredRows(): TeamMember[] {
    return (this.dataSource && this.dataSource.filteredData) ? this.dataSource.filteredData : this.dataSource.data;
  }

  createFilterPredicate(): (data: TeamMember, filter: string) => boolean {
    return (data: TeamMember, filter: string): boolean => {
      const { status, search, emailStatus, role, isRespondent, custom } = JSON.parse(filter || '{}');
      const normalizedStatus = (status || 'ALL').toUpperCase();
      const normalizedSearch = (search || '').trim().toLowerCase();
      const selectedEmailStatuses: string[] = emailStatus || [];
      const selectedRoles: string[] = role || [];
      const selectedIsRespondent: boolean[] = isRespondent || [];

      if (normalizedStatus !== 'ALL' && data.response_status !== normalizedStatus) return false;

      if (selectedEmailStatuses.length > 0) {
        const memberEmailStatus = data.last_status_invitation_email;
        if (!memberEmailStatus || !selectedEmailStatuses.includes(memberEmailStatus)) return false;
      }

      if (selectedRoles.length > 0) {
        if (!data.role || !selectedRoles.includes(data.role)) return false;
      }

      if (selectedIsRespondent.length > 0) {
        const respondentVal = data.is_respondent !== false;
        if (!selectedIsRespondent.includes(respondentVal)) return false;
      }

      if (normalizedSearch) {
        const searchable = [data.name, data.last_name, data.email];
        const match = searchable.filter(v => !!v).some(v => String(v).toLowerCase().includes(normalizedSearch));
        if (!match) return false;
      }

      // Custom fields filtering
      if (custom && Object.keys(custom).length > 0) {
        for (const fieldId of Object.keys(custom)) {
          const field = this.teamMemberCustomFields.find(f => f.id == fieldId);
          if (!field) continue;

          const filterConfig = custom[fieldId];
          const val = (data as any).customs?.team_member_custom_fields?.[field.id];

          if (field.type === 'boolean') {
            if (filterConfig !== 'ALL') {
              const expected = filterConfig === 'YES';
              if (val !== expected) return false;
            }
          } else if (field.type === 'number' || field.type === 'date') {
            const { min, max } = filterConfig;

            // Un filtro è attivo solo se è un numero o una data (esclude null, undefined, stringa vuota)
            const isMinActive = min !== null && min !== undefined && min !== '';
            const isMaxActive = max !== null && max !== undefined && max !== '';

            let compareVal = val;
            let compareMin = min;
            let compareMax = max;

            if (field.type === 'date') {
              compareVal = val ? new Date(val).getTime() : undefined;
              compareMin = min ? new Date(min).getTime() : undefined;
              compareMax = max ? new Date(max).getTime() : undefined;

              // Per le date, ignoriamo le ore nel confronto se necessario o confrontiamo timestamp
              if (compareVal) compareVal = new Date(new Date(compareVal).setHours(0, 0, 0, 0)).getTime();
              if (compareMin) compareMin = new Date(new Date(compareMin).setHours(0, 0, 0, 0)).getTime();
              if (compareMax) compareMax = new Date(new Date(compareMax).setHours(23, 59, 59, 999)).getTime();
            }

            if (isMinActive && (compareVal === undefined || compareVal === null || compareVal < compareMin)) return false;
            if (isMaxActive && (compareVal === undefined || compareVal === null || compareVal > compareMax)) return false;
          }
          else if (field.type === 'text') {
            if (filterConfig && filterConfig.length > 0 && !filterConfig.includes(val)) return false;
          }
        }
      }

      return true;
    };
  }

  getUniqueCustomValues(field: any): string[] {
    const values = this.members.map(m => (m as any).customs?.team_member_custom_fields?.[field.id])
      .filter(v => v !== undefined && v !== null && v !== '');
    return Array.from(new Set(values)).sort();
  }

  private renderChart(): void {
    const members = this.members || [];
    const respondents = members.filter(m => m.is_respondent === true);

    this.answeredCount = respondents.filter(m => m.response_status === 'SENT').length;
    this.draftCount = respondents.filter(m => m.response_status === 'DRAFT').length;
    this.notAnsweredCount = respondents.length - this.answeredCount - this.draftCount;

    const activeSlices = [this.answeredCount, this.notAnsweredCount, this.draftCount].filter(count => count > 0).length;

    this.chartOptions = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const label = params.name.replace(/\s*\(\d+\)\s*$/, '');
          return `${label}: ${params.value}`;
        }
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#666' }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: '50%',
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: activeSlices > 1 ? 2 : 0
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 14
          },
          labelLine: {
            show: false
          },
          data: [
            {
              value: this.answeredCount,
              name: `${this.translateService.instant('generic.who-has-responded')} (${this.answeredCount})`,
              itemStyle: { color: '#27ae60' }
            },
            {
              value: this.notAnsweredCount,
              name: `${this.translateService.instant('generic.who-has-not-responded')} (${this.notAnsweredCount})`,
              itemStyle: { color: '#e57373' }
            },
            {
              value: this.draftCount,
              name: `${this.translateService.instant('generic.answers.draft')} (${this.draftCount})`,
              itemStyle: { color: '#ffb74d' }
            },
          ].filter(item => item.value > 0) // Filter out 0 value items completely to avoid artifacts
        }
      ]
    };
  }

  emailResponsesAvailable(row): boolean {
    return row.last_status_invitation_email !== 'not-yet-sent';
  }

  impersonable(member): boolean {
    return ['SENT', 'DRAFT'].includes(member.response_status);
  }

  viewSentQuestionnaire(member): void {
    this.dialog.open(ModalViewSentSurveyQuestionnaireComponent, {
      panelClass: 'fullscreen-dialog',
      data: {
        surveyID: this.surveyId,
        impersonateEmail: member.email,
      },
    });
  }

  get answersSent() {
    return this.dataSource?.data?.filter((m) => m.is_respondent === true && m.response_status === 'SENT');
  }

  get participantsCount() {
    return this.members?.filter(m => m.is_respondent === true).length || 0;
  }

  toggleRow(element: TeamMember) {
    this.expandedElement = (this.expandedElement === element) ? null : element;
    if (this.expandedElement !== element) {
      this.editingMember = null;
    }
  }

  onEditMember(member: TeamMember) {
    this.editingMember = JSON.parse(JSON.stringify(member));
    if (!this.editingMember.customs) {
      this.editingMember.customs = { team_member_custom_fields: {} };
    }
    if (!this.editingMember.customs.team_member_custom_fields) {
      this.editingMember.customs.team_member_custom_fields = {};
    }
    // ensure all custom fields exist in the editingMember object
    this.teamMemberCustomFields.forEach(field => {
      if (this.editingMember.customs.team_member_custom_fields[field.id] === undefined) {
        this.editingMember.customs.team_member_custom_fields[field.id] = null;
      }
    });
  }

  onCancelEdit() {
    this.editingMember = null;
  }

  openImportModal() {
    this.dialog.open(ModalImportSurveyTeamComponent, {
      panelClass: 'medium-dialog',
      data: {
        surveyId: this.surveyId,
        teamMemberCustomFields: this.teamMemberCustomFields
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        if (result.imported && Array.isArray(result.imported)) {
          const importedWithIds = result.imported.map((m, idx) => ({
            ...m,
            id: `temp_${Date.now()}_${idx}`
          }));
          this.members = [...this.members, ...importedWithIds];
          this.dataSource.data = this.members;
          this.renderChart();
          
          // Emit refresh so parent component picks up structural changes if needed
          this.refresh.emit();
        } else {
          this.refresh.emit();
        }
      }
    });
  }

  onSaveMember(updatedMember?: any) {
    if (updatedMember) {
      this.editingMember = updatedMember;
    }
    if (!this.surveyId || !this.editingMember) return;

    this.loading = true;
    // Prepare the payload to include team_member_custom_fields and phone_number
    const customsPayload: any = {
      ...this.editingMember.customs,
      team_member_custom_fields: {}
    };

    this.teamMemberCustomFields.forEach(cf => {
      if (this.editingMember.customs?.team_member_custom_fields?.[cf.id] !== undefined) {
        customsPayload.team_member_custom_fields[cf.id] = this.editingMember.customs.team_member_custom_fields[cf.id];
      }
    });

    const payload = {
      name: this.editingMember.name,
      last_name: this.editingMember.last_name,
      email: this.editingMember.email,
      role: this.editingMember.role,
      is_respondent: !!this.editingMember.is_respondent,
      customs: customsPayload
    };

    const memberId = this.editingMember.id;

    this.surveyService.updateSurveyTeamMember(
      this.surveyId.toString(),
      memberId.toString(),
      payload,
      (response) => {
        this.loading = false;
        this.toast.success(this.translateService.instant('generic.success'), this.translateService.instant('components.surveys.survey-team.survey-team.update-team-member-successfully'));

        // Update the local member data
        const index = this.members.findIndex(m => m.id === memberId);
        if (index !== -1) {
          this.members[index] = {
            ...this.members[index],
            name: payload.name,
            last_name: payload.last_name,
            email: payload.email,
            role: payload.role,
            is_respondent: payload.is_respondent,
            customs: response.data?.customs || payload.customs
          };
          this.dataSource.data = [...this.members];
        }

        this.editingMember = null;
        this.expandedElement = null;
        this.cdr.detectChanges();
      },
      (err) => {
        this.loading = false;
        console.error(err);
        this.toast.error(this.translateService.instant('SURVEY.MEMBER_UPDATE_ERROR'));
      }
    );
  }
}
